import { Page } from "@components-common/layouts";
import { Widget, WidgetChart } from "@components-common/widgets";
import { Col, Loader, Row } from "taltech-styleguide";
import { RootieTable } from "../../../components-common/tables/RootieTable";
import { useLocation, useNavigate } from "react-router-dom";
import { FacetedChart } from "@components-common/charts";
import { application } from "../../../_runtime/bootstrap";
import { useEffect, useState } from "react";
import { initialCourse } from "@components-domain/education";
import { InformationWidget } from "../../../components-common/widgets/InformationWidget";
import {
  Button,
  Button as TaltechButton,
  BUTTON_COLOR,
  ICONS,
} from "taltech-styleguide";

export const CompleteCourseButton = ({ course, nav }: any) => {
  return course.state === "COMPLETED" ? (
    <TaltechButton
      variant={BUTTON_COLOR.PRIMARY}
      iconRight={ICONS.SUCCESS}
      disabled={true}
      size="sm"
    >
      Completed
    </TaltechButton>
  ) : (
    <TaltechButton
      variant={BUTTON_COLOR.SECONDARY}
      iconRight={ICONS.SUCCESS}
      onClick={() =>
        application
          .getHttpClient()
          .put(`/coursesms/courses/complete/${course.id}`, course)
          .then(() => nav(-1))
      }
      size="sm"
    >
      Complete Course
    </TaltechButton>
  );
};

/**
 * Component: DashboardPageCourses
 *
 * This component displays a dashboard with course information, student and lecturer lists, and assignment statistics.
 *
 * @param {Props} props - The component props.
 *
 * @returns {JSX.Element} The rendered DashboardPageCourses component.
 */
export const DashboardPageCourses: React.FC<any> = (props) => {
  const [course, setCourse] = useState<any>(initialCourse);
  const guidRegex = /[\da-zA-Z]{8}-([\da-zA-Z]{4}-){3}[\da-zA-Z]{12}/g;
  const { pathname } = useLocation();
  const nav = useNavigate();
  let courseId = "";
  if (pathname.match(guidRegex)) {
    courseId = pathname.match(guidRegex)![0];
  }

  useEffect(() => {
    application
      .getHttpClient()
      .get(`/coursesms/courses/${courseId}`)
      .then((res: any) => {
        setCourse(res.body);
      });
  }, []);

  const visibleFields: string[] = [
    "picture",
    "number",
    "name",
    "description",
    "startDate",
    "endDate",
    "state",
    "createdDate",
  ];

  const infoWidget: any = {
    data: course,
    visibleFields: visibleFields,
    widgetProps: {
      id: "course_info",
      title: (
        <div className="d-flex justify-content-between align-items-center">
          Course Information
          {course.state !== "ARCHIVED" && (
            <CompleteCourseButton
              {...{
                course: course,
                nav: (to: any) => nav(to),
              }}
            />
          )}
        </div>
      ),
      subTitle: "",
      className: "",
    },
  };

  const tableWidgets = [
    {
      title: "Students List",
      subTitle: "",
      tableProps: {
        source: {
          type: "REMOTE",
          url: `/coursesms/members/students`,
          urlParams: {
            ["courseId"]: courseId,
          },
          key: "students",
        },
        columns: ["email", "createdDate"],
        showPagination: false,
        showRowActions: false,
        options: {
          pagination: false,
        },
      },
    },
    {
      title: "Lecturers List",
      subTitle: "",
      tableProps: {
        source: {
          type: "REMOTE",
          url: `/coursesms/members/lecturers`,
          urlParams: {
            ["courseId"]: courseId,
          },
          key: "lecturers",
        },
        columns: ["email", "createdDate"],
        showPagination: false,
        showRowActions: false,
        options: {
          pagination: false,
        },
      },
    },
  ];

  const chartOptions: any = {
    id: "chart_assignment_submissions_by_students",
    firstBarData: {
      name: "Active",
      propName: "activeCount",
    },
    secondBarData: {
      name: "Submitted",
      propName: "submittedCount",
    },
    xAxisProperty: "assignmentTitle",
    title: "",
    subTitle: "",
    urlEndpoint: `analyticsms/course-analytics/get-assignments-info-by-course-id?courseId=${courseId}`,
  };

  const widgetChartProps = {
    widgetProps: {
      id: "assignments_statistics",
      title: "Count of submitted and active submissions per assignment",
      subTitle: "",
      className: "",
    },
    chartProps: {
      ChartComponent: FacetedChart,
      chartOptions,
    },
  };

  return (
    <Page title="Dashboard">
      {course.id === "" ? (
        <Loader overlay />
      ) : (
        <Row>
          <Col lg="4" className="d-flex">
            <InformationWidget key={course.id} {...infoWidget} />
          </Col>
          <Col lg="8" className="d-flex flex-column justify-content-between">
            <Row className="pb-2">
              {tableWidgets.map((widget: any) => (
                <Col key={widget.title} sm="12" lg="6">
                  <Widget title={widget.title} subTitle={widget.subTitle}>
                    <div
                      style={{
                        height: "295px",
                        maxHeight: "295px",
                        overflow: "auto",
                      }}
                    >
                      <RootieTable {...widget.tableProps} />
                    </div>
                  </Widget>
                </Col>
              ))}
            </Row>
            <Row className="pt-2">
              <Col>
                <WidgetChart {...widgetChartProps} />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </Page>
  );
};
